import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    ContactSliceModel, GetContactsResponse,
    ContactDetailed, DeleteContactResponse, ActivateContactResponse, DeactivateContactResponse,
    CreateContactResponse, CreateContactDataResponse, UpdateContactResponse, ContactRequest, GetContactsByCompanyIdsRequest,
    GetContactDraftsResponse, GetContactDraftResponse, ContactDraft,
    ConfirmContactDraftRequest, ConfirmContactDraftResponse, DiscardContactDraftResponse
} from "./contact-interfaces";
import {
    RejectedValue, ErrorResponse, ImportHubspotListResponse, FilteredDropDownRequest, FilteredDropDownResponse,
    FilteredDropDownDetailResponse, FilteredDropDownModelInit, TableQueryParams, MainResponse
} from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { GetPartnerLocationsResponse } from "../../Partners/state/partner-interfaces";
import { ApplicantResponse } from "../../../ApplicantTracking/state/applicant-tracking-interfaces";
import { GetAssignedApplicants } from "../../../ApplicantTracking/state/applicantTrackingSlice";
import { downloadBlob, extractVcfFullName } from "shared/utils/common";
import { API_BASE_URL as baseUri } from "config/env";

const initialValue: ContactSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    isModalSubmited: false,
    message: '',
    showNotification: false,
    activeContactsCount: 0,
    activeContacts: [],
    inactiveContactsCount: 0,
    inactiveContacts: [],
    contact: null,
    contactToShow: null,
    genders: [],
    articles: [],
    locations: [],
    createContactData: null,
    importContacts: {
        importedContactsResponse: null,
        isLoading: false
    },
    filteredPartnersList: FilteredDropDownModelInit,
    draftContacts: [],
}

/**
 * Plain (non-thunk) fetchers used by Excel export. They deliberately bypass the slice so that
 * pulling every row for the export does not disturb the page currently on screen.
 */
async function fetchAllContacts(url: string, params: TableQueryParams): Promise<ContactDetailed[]> {
    const response = await fetch(baseUri + url, {
        method: "POST",
        headers: AuthHeader(),
        body: JSON.stringify(params)
    });
    if (response.status !== 200) return [];
    const responseModel = await response.json() as GetContactsResponse;
    return responseModel.contacts ?? [];
}

export const fetchAllActiveContactsForExport = (params: TableQueryParams) => fetchAllContacts('api/contacts', params);
export const fetchAllInactiveContactsForExport = (params: TableQueryParams) => fetchAllContacts('api/contacts/inactive', params);

// GET ACTIVE CONTACTS
export const GetActiveContacts = createAsyncThunk<GetContactsResponse, TableQueryParams, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetActiveContacts',
    async (params: TableQueryParams, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(params)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetContactsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET ACTIVE CONTACTS BY COMPANY ID'S

export const GetContactsByCompanyIds = createAsyncThunk<GetContactsResponse, GetContactsByCompanyIdsRequest, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetContactsByCompanyIds',
    async (getContactsByCompanyIdsRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/get-by-company-ids', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(getContactsByCompanyIdsRequest)
        });
        console.log('Response status:', response);
        if (response.status === 200) {
            console.log('usao u 200');
            const responseModel = await response.json() as GetContactsResponse;
            return responseModel;
        }
        else {
            console.log('usao u else');
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET CONTACT DRAFTS
// All of the corporation's drafts at once, newest first; the list is filtered and paged on screen.
export const GetContactDrafts = createAsyncThunk<GetContactDraftsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetContactDrafts',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drafts', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetContactDraftsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET CONTACT DRAFT BY ID
// The details page and modal keep the draft in their own state, so this thunk has no reducers.
export const GetContactDraft = createAsyncThunk<ContactDraft, string, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetContactDraft',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drafts/get?contactId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetContactDraftResponse;
            return responseModel.draft;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// CONFIRM CONTACT DRAFT
// Turns the draft into a regular contact; the response holds the new contact's id, which is the
// draft's id. The caller shows the result, the reducer only takes the draft off the list.
export const ConfirmContactDraft = createAsyncThunk<ConfirmContactDraftResponse, ConfirmContactDraftRequest, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/ConfirmContactDraft',
    async (request: ConfirmContactDraftRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drafts/confirm', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ConfirmContactDraftResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// DISCARD CONTACT DRAFT
// The caller shows the result, the reducer only takes the draft off the list.
export const DiscardContactDraft = createAsyncThunk<DiscardContactDraftResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/DiscardContactDraft',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drafts/discard?contactId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DiscardContactDraftResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET INACTIVE CONTACTS
export const GetInactiveContacts = createAsyncThunk<GetContactsResponse, TableQueryParams, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetInactiveContacts',
    async (params: TableQueryParams, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/inactive', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(params)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetContactsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// CREATE CONTACT
export const CreateContact = createAsyncThunk<CreateContactResponse, ContactRequest, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/CreateContact',
    async (contact: ContactRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(contact)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreateContactResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// ACTIVATE CONTACT
export const ActivateContact = createAsyncThunk<ActivateContactResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/ActivateContact',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/activate?contactId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivateContactResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// DEACTIVATE CONTACT
export const DeactivateContact = createAsyncThunk<DeactivateContactResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/DeactivateContact',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/deactivate?contactId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DeactivateContactResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// DELETE PARTNER
export const DeleteContact = createAsyncThunk<DeleteContactResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/DeleteContact',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/delete?contactId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DeleteContactResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET CREATE CONTACT DATA
export const GetCreateContactData = createAsyncThunk<CreateContactDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetCreateContactData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/get-create-contact-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreateContactDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// UPDATE CONTACT 
export const UpdateContact = createAsyncThunk<UpdateContactResponse, ContactRequest, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/UpdateContact',
    async (contact: ContactRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(contact)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateContactResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET CONTACT UPDATE DATA
export const GetContact = createAsyncThunk<ContactRequest, string, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetContact',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/get-update-data?contactId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ContactRequest;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);



// Export contact as VCF
export const ExportContactVCF = createAsyncThunk<void, { id: string; legacy?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/ExportContactVCF',
    async ({ id, legacy }, { rejectWithValue }) => {
        // legacy=true asks the backend for an ISO-8859-1/Windows-1252 encoded vCard that renders
        // umlauts correctly in Windows Contacts and classic Outlook (which ignore the charset
        // declaration and use the system codepage). Default is the modern UTF-8 vCard.
        const url = baseUri + 'api/contacts/export-vcf?contactId=' + id + (legacy ? '&legacy=true' : '');
        const response = await fetch(url, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const buffer = await response.arrayBuffer();
            // The body is 7-bit ASCII (quoted-printable) in both formats; extractVcfFullName decodes
            // the FN value according to the CHARSET declared on that line.
            const text = new TextDecoder('utf-8').decode(new Uint8Array(buffer));
            const fullName = extractVcfFullName(text);
            const blob = new Blob([new Uint8Array(buffer)], { type: 'text/vcard' });
            downloadBlob(blob, `${fullName}.vcf`);
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// Import Hubspot Contacts
export const ImportHubspotContacts = createAsyncThunk<ImportHubspotListResponse, FormData, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/ImportHubspotContacts',
    async (request: FormData, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/import/hubspot', {
            body: request,
            method: "POST",
            headers: AuthHeader(false)
        });
        if (response.ok) {
            const responseModel = await response.json() as ImportHubspotListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

//Get FIltered partner list
export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//Get company locations
export const GetCompanyLocations = createAsyncThunk<GetPartnerLocationsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ContactsSlice/GetCompanyLocations',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-location/by-partner?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as GetPartnerLocationsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateContactFromApplicant = createAsyncThunk<MainResponse, ContactRequest, { rejectValue: RejectedValue }>(
    'Slice/ContactSlice/CreateContactFromApplicant',
    async (contact: ContactRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/create-contact', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(contact)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetAssignedApplicants());
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// Suggestions for the duplicate check on the contact form. The options carry the company of the contact
// as detail, which is merged into the label so two contacts with the same name can be told apart. The
// company itself already contains a dash ("BIS - BIMEXPERTS"), so a bullet separates it from the name.
export const GetContactsForSuggestionList = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/FilteredListsSlice/GetContactsForSuggestionList',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownDetailResponse;
            return {
                ...getResponse,
                data: getResponse.data.map((option) => ({
                    ...option,
                    label: option.detail ? option.label + " • " + option.detail : option.label
                }))
            };
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ContactSlice = createSlice({
    name: 'ContactsSlice',
    initialState: initialValue,
    reducers: {
        resetContactSliceState(state) {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.isModalSubmited = false;
            state.message = '';
            state.showNotification = false;
            state.activeContacts = [];
            state.inactiveContacts = [];
            state.draftContacts = [];
            state.contact = null;
            state.contactToShow = null;
            state.genders = [];
            state.articles = [];
            state.createContactData = null;
            state.importContacts = {
                importedContactsResponse: null,
                isLoading: false
            };
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        resetSubmitModalValue: (state) => {
            state.isModalSubmited = false;
        },
        resetContactState: (state) => {
            state.contact = null;
            state.contactToShow = null;
            state.isModalLoading = false;
        },
        resetContactEditState: (state) => {
            state.contact = null;
            state.isModalLoading = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetCreateContactData: (state) => {
            state.createContactData = null;
            state.isModalLoading = false;
        },
        resetContactsImport: (state) => {
            state.importContacts = {
                importedContactsResponse: null,
                isLoading: false
            }
        },
        setSelectedPartners: (state, action: PayloadAction<string>) => {
            if (state.contact) {
                state.contact.partnerId = action.payload;
            }
        },
        setPartnerLocationId: (state, action: PayloadAction<string | null>) => {
            if (state.contact) {
                state.contact.partnerLocationId = action.payload;
            }
        },
        clearLocationList: (state) => {
            state.locations = [];
        },
        setContactFromApplicant(state, action: PayloadAction<ApplicantResponse>) {
            const applicant = action.payload;
            state.isModalLoading = false;

            state.contact = {
                id: applicant.id ?? null,
                firstName: applicant.firstName,
                lastName: applicant.lastName,
                privateEmail: applicant.email ?? null,
                privatePhoneNumber: applicant.phoneNumber ?? null,
                //second email
                genderId: applicant.genderId ?? 0,
                dateOfBirth: applicant.dateOfBirth ?? null,
                title: applicant.title || null,
                titleAfter: applicant.titleAfter || null,
                countryId: applicant.countryId ?? null,
                city: applicant.city || '',
                streetAndNumber: applicant.address || '',
                department: "",
                position: "",
                email: "",
                linkedInProfile: "",
                workingCity: "",
                preferredLanguages: [],
                partnerId: "",
                salutationSuffixes: [],
                nameDay: null,
                hiringRequestId: applicant.hiringRequestId
            };
        },
        // Fills the contact form from a draft being confirmed.
        setContactFromDraft(state, action: PayloadAction<ContactRequest>) {
            state.isModalLoading = false;
            state.contact = action.payload;
        }
    },
    extraReducers(builder) {
        // Get active contacts
        builder.addCase(GetActiveContacts.fulfilled, (state, action: PayloadAction<GetContactsResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.activeContactsCount = action.payload.count;
            state.activeContacts = action.payload.contacts;
        })
            .addCase(GetActiveContacts.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetActiveContacts.pending, state => {
                // state.isLoading = true;
            })

        // Get inactive contacts
        builder.addCase(GetInactiveContacts.fulfilled, (state, action: PayloadAction<GetContactsResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.inactiveContactsCount = action.payload.count;
            state.inactiveContacts = action.payload.contacts;
        })
            .addCase(GetInactiveContacts.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetInactiveContacts.pending, state => {
                // state.isLoading = true;
            })

        // Get contact drafts
        builder.addCase(GetContactDrafts.fulfilled, (state, action: PayloadAction<GetContactDraftsResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.draftContacts = action.payload?.drafts ?? [];
        })
            .addCase(GetContactDrafts.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })

        // A confirmed or discarded draft is no longer a draft.
        builder.addCase(ConfirmContactDraft.fulfilled, (state, action) => {
            state.draftContacts = state.draftContacts.filter(draft => draft.id !== action.meta.arg.id);
        })
        builder.addCase(DiscardContactDraft.fulfilled, (state, action) => {
            state.draftContacts = state.draftContacts.filter(draft => draft.id !== action.meta.arg);
        })

        // Activate contact
        builder.addCase(ActivateContact.fulfilled, (state, action: PayloadAction<ActivateContactResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.isModalLoading = false;
            state.isModalSubmited = true;
            state.showNotification = true;
            state.statusCode = '200';
        })
            .addCase(ActivateContact.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ActivateContact.pending, state => {
                state.isLoading = true;
            })

        // Deactivate contact
        builder.addCase(DeactivateContact.fulfilled, (state, action: PayloadAction<DeactivateContactResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.isModalLoading = false;
            state.isModalSubmited = true;
            state.showNotification = true;
            state.statusCode = '200';
        })
            .addCase(DeactivateContact.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeactivateContact.pending, state => {
                state.isLoading = true;
            })

        // Delete contact
        builder.addCase(DeleteContact.fulfilled, (state, action: PayloadAction<DeleteContactResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.isModalLoading = false;
            state.isModalSubmited = true;
            state.showNotification = true;
            state.statusCode = '200';
        })
            .addCase(DeleteContact.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteContact.pending, state => {
                state.isLoading = true;
            })

        // Create contact
        builder.addCase(CreateContact.fulfilled, (state, action: PayloadAction<CreateContactResponse>) => {
            state.message = action.payload.message;
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        }).addCase(CreateContact.rejected, (state, action) => {
            state.isModalLoading = false;
            state.message = action.payload?.message ?? 'Error in application';
            state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            state.showNotification = true;
        }).addCase(CreateContact.pending, state => {
            state.isModalLoading = true;
        })

        // Update contact
        builder.addCase(UpdateContact.fulfilled, (state, action: PayloadAction<UpdateContactResponse>) => {
            state.message = action.payload.message;
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        }).addCase(UpdateContact.rejected, (state, action) => {
            state.isModalLoading = false;
            state.message = action.payload?.message ?? 'Error in application';
            state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            state.showNotification = true;
        }).addCase(UpdateContact.pending, state => {
            state.isModalLoading = true;
        })

        // Get create contact data
        builder.addCase(GetCreateContactData.fulfilled, (state, action: PayloadAction<CreateContactDataResponse>) => {
            state.isModalLoading = false;
            state.createContactData = action.payload;
            state.message = '';
            /* state.statusCode = '200'; */
        })
            .addCase(GetCreateContactData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCreateContactData.pending, state => {
                state.isModalLoading = true;
            })

        // Get contact
        builder.addCase(GetContact.fulfilled, (state, action: PayloadAction<ContactRequest>) => {
            state.isModalLoading = false;
            state.contact = action.payload;
            state.contactToShow = action.payload;
            state.message = '';
            /* state.statusCode = '200'; */
        })
            .addCase(GetContact.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetContact.pending, state => {
                state.isModalLoading = true;
            })
        // Export Contact VCF
        builder.addCase(ExportContactVCF.fulfilled, (state) => {
            state.isModalLoading = false;
            state.message = '';
        })
            .addCase(ExportContactVCF.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ExportContactVCF.pending, state => {
                state.isModalLoading = true;
            })
        // Import Hubspot Partners
        builder
            .addCase(ImportHubspotContacts.fulfilled, (state, action: PayloadAction<ImportHubspotListResponse>) => {
                state.importContacts.isLoading = false;
                state.importContacts.importedContactsResponse = action.payload;
                state.message = action.payload.message;
                state.statusCode = "200";
            })
            .addCase(ImportHubspotContacts.rejected, (state, action) => {
                state.importContacts.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ImportHubspotContacts.pending, state => {
                state.importContacts.isLoading = true;
            })
        //Get Filtered Partners list    
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.filteredPartnersList.loading = true;
            state.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
        // GET LOCATIONS
        builder.addCase(GetCompanyLocations.fulfilled, (state, action: PayloadAction<GetPartnerLocationsResponse>) => {
            state.isModalLoading = false;
            state.locations = action.payload.locations.map(location => ({
                value: location.id,
                label: location.isPrimary ? `${location.locationName} - ${location.streetAndNumber}, ${location.zipCode} ${location.city}, ${location.country}` :
                    `Branch office - ${location.streetAndNumber}, ${location.zipCode} ${location.city}, ${location.country}`,
                isActive: location.isActive
            }));
        })
            .addCase(GetCompanyLocations.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCompanyLocations.pending, state => {
                state.isModalLoading = true;
            })
        //CreateContactFromApplicant
        builder.addCase(CreateContactFromApplicant.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.message = 'The contact is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateContactFromApplicant.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateContactFromApplicant.pending, state => {
                state.isModalLoading = true;
            })
    }
})

export const { resetContactSliceState, resetSubmitModalValue, resetContactState, resetContactEditState, resetStatusCode,
    resetCreateContactData, resetContactsImport, setSelectedPartners, setPartnerLocationId, clearLocationList, setContactFromApplicant, setContactFromDraft } = ContactSlice.actions;
export default ContactSlice.reducer;